import { getChangeAffectedElementId } from './action.helpers';
import {
  getElementsWithCategoryStructureDriverWithoutEnrich,
  getIncomingCategoryEnrichElementIds,
  getPropertySlotKey,
} from './condense-changes.helpers';
import { resolveVersionForProposal } from './resolve-version-for-proposal.helpers';
import { stripRecipeResolvedElement } from './recipe-resolved-element-cache.helpers';
import { isRedundantElementRecipeApplyChange } from '@shared/helpers/recipe-apply-payload.helpers';
import { projectChangeToElementPartial } from '@shared/helpers/pipeline/updates';
import {
  getElementQuantityRecord,
  isElementQuantityExpressionProperty,
} from '@shared/helpers/element-quantity.helpers';
import type { ElementQuantityName } from '@shared/models/element-quantities.model';
import {
  isElementQuantityValueChangeNoopForElement,
  isFormulaDrivenExpressionQuantityCount,
  mustPersistStaleExpressionQuantityValueOverride,
} from '@shared/helpers/pipeline/updates/element-quantity.update';
import { compare } from '@shared/helpers/compare.helpers';
import { isExpressionValue } from '@shared/helpers/expression-factory.helpers';
import { getElementCategoryId } from '@shared/helpers/element-category-id.helpers';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import { updateBuildingVersionProperties } from '@shared/helpers/project.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import { getItemById } from '@shared/helpers/array.helpers';
import { cacheFactory } from '@shared/helpers/function.helpers';
import {
  ProjectChangeType,
  type Action,
  type ProjectChange,
  type ProjectChangeMap,
} from '@shared/helpers/pipeline/update-engine.models';
import {
  flattenElements,
  forEachElement,
  getAllBuildingVersions,
  getBuildingVersionById,
  getElementById,
  getPathToElement,
  isBuildingVersionElement,
  isElement,
} from '@shared/helpers/recursive-element.helpers';
import { getProductElementOverrideChangeOverlapSlots } from '@shared/helpers/pipeline/product-element-override-changes.helpers';
import { getRecipeScopedParentElementId } from '@shared/helpers/recipe.helpers';
import type {
  Baseline,
  OneOfElements,
  OneOfParentElements,
  Project,
} from '@shared/models/project.model';
import type { IProposal, ProposalId } from '@shared/models/proposals.model';

/**
 * Element-visibility changes share a single conceptual slot regardless of direction
 * (an `ElementActivate` overrides a previous `ElementDeactivate` for the same element and vice versa).
 */
const ELEMENT_VISIBILITY_SLOT = 'element.visibility';

export {
  productElementCarbonLevelOverlapSlot,
  productElementOverrideOverlapSlot,
} from '@shared/helpers/pipeline/product-element-override-changes.helpers';

const isGranularProductElementOverrideChange = (
  change: ProjectChange,
): boolean =>
  change.type === ProjectChangeType.ProductElementTransportOverride ||
  change.type === ProjectChangeType.ProductElementWasteOverride ||
  change.type === ProjectChangeType.ProductElementCarbonLevelOverride;

/**
 * Returns a normalized "slot" identifier used to detect when one change overrides another
 * across actions.
 *
 * Authoritative property/quantity edits (resolved value, expression, counts, etc.) for the
 * same field name share one slot (`property:${name}` / `quantity:${name}`), unlike
 * {@link getPropertySlotKey}, which scopes by change type. That way an expression edit and
 * a later resolved edit still subtract the earlier change even though the types differ.
 *
 * Legacy {@link ProjectChangeType.ElementPropertyFormatted} and
 * {@link ProjectChangeType.ElementQuantityFormatted} are intentionally **not** folded into
 * those slots: formatted display is derived when the underlying value changes, so we do not
 * treat formatted rows as a parallel edit stream that should subsume or be subsumed by
 * resolved/expression overlap logic. For them, this function falls through to
 * {@link getPropertySlotKey} (type-scoped keys such as `element.property.formatted:${name}`).
 *
 * Returns `undefined` when the change has no overlap semantics (and therefore should not
 * subtract anything from previous actions).
 */
export const getOverlapSlotKey = (
  change: ProjectChange,
): string | undefined => {
  if (
    change.type === ProjectChangeType.ElementActivate ||
    change.type === ProjectChangeType.ElementDeactivate ||
    change.type === ProjectChangeType.ElementSoftDelete
  ) {
    return ELEMENT_VISIBILITY_SLOT;
  }
  // Authoritative property/quantity edits share one slot per name; legacy *Formatted uses
  // getPropertySlotKey in default (see JSDoc).
  switch (change.type) {
    case ProjectChangeType.ElementPropertyResolved:
    case ProjectChangeType.ElementPropertyExpression:
    case ProjectChangeType.ElementPropertyValue:
    case ProjectChangeType.ElementPropertyFallbackCount:
    case ProjectChangeType.ElementPropertyCount:
      return `property:${change.propertyName}`;
    case ProjectChangeType.ElementQuantityResolved:
    case ProjectChangeType.ElementQuantityExpression:
    case ProjectChangeType.ElementQuantityFallbackExpression:
    case ProjectChangeType.ElementQuantityFallbackCount:
    case ProjectChangeType.ElementQuantityValue:
    case ProjectChangeType.ElementQuantityCount:
      return `quantity:${change.quantityName}`;
    case ProjectChangeType.ElementCategoryEnrich:
      return change.type;
    case ProjectChangeType.ElementRecipeApply:
    case ProjectChangeType.ElementRecipeDetach:
    case ProjectChangeType.ElementRecipeClear:
      // One structural recipe slot per element — detach/clear must replace a prior apply.
      return 'recipe';
    default:
      return getPropertySlotKey(change);
  }
};

/**
 * All overlap slot keys for a change. Product overrides emit one key per target.
 */
export const getOverlapSlotKeys = (change: ProjectChange): string[] => {
  const overrideSlots = getProductElementOverrideChangeOverlapSlots(change);
  if (overrideSlots.length > 0) return overrideSlots;
  const slot = getOverlapSlotKey(change);
  return slot ? [slot] : [];
};

/**
 * At most one {@link ProjectChangeType.ElementCategoryEnrich} per element; keeps the last
 * in array order.
 */
/**
 * Within one action batch, keeps only the last change per
 * `${elementId}:${overlapSlot}:${change.type}`. Different change types on the same
 * quantity slot (e.g. value + expression) are preserved.
 */
const AUTHORITATIVE_VALUE_OVERRIDE_TYPES = new Set<ProjectChangeType>([
  ProjectChangeType.ElementPropertyValue,
  ProjectChangeType.ElementQuantityValue,
]);

const REDUNDANT_RESOLVED_WITH_OVERRIDE_TYPES = new Set<ProjectChangeType>([
  ProjectChangeType.ElementPropertyResolved,
  ProjectChangeType.ElementQuantityResolved,
]);

/**
 * Drops {@link ProjectChangeType.ElementQuantityResolved} /
 * {@link ProjectChangeType.ElementPropertyResolved} rows when the same element+slot
 * already has a value-override change in the batch (pipeline emits both; only the
 * override is needed for proposal storage and condense).
 */
export const dedupeRedundantResolvedWhenOverridePresentWithinAction = (
  changes: readonly ProjectChange[],
): ProjectChange[] => {
  const slotsWithValueOverride = new Set<string>();
  for (const change of changes) {
    if (!AUTHORITATIVE_VALUE_OVERRIDE_TYPES.has(change.type)) continue;
    const elementId = getChangeAffectedElementId(change);
    const slot = getOverlapSlotKey(change);
    if (!elementId || !slot) continue;
    slotsWithValueOverride.add(`${elementId}:${slot}`);
  }
  if (slotsWithValueOverride.size === 0) return [...changes];

  return changes.filter((change) => {
    if (!REDUNDANT_RESOLVED_WITH_OVERRIDE_TYPES.has(change.type)) return true;
    const elementId = getChangeAffectedElementId(change);
    const slot = getOverlapSlotKey(change);
    if (!elementId || !slot) return true;
    return !slotsWithValueOverride.has(`${elementId}:${slot}`);
  });
};

export const dedupeSameSlotSameTypeChangesWithinAction = (
  changes: readonly ProjectChange[],
): ProjectChange[] => {
  const lastIndexByKey = new Map<string, number>();
  for (let i = 0; i < changes.length; i++) {
    const change = changes[i];
    if (!change) continue;
    const elementId = getChangeAffectedElementId(change);
    const slot = getOverlapSlotKey(change);
    if (!elementId || !slot) continue;
    lastIndexByKey.set(`${elementId}:${slot}:${change.type}`, i);
  }
  if (lastIndexByKey.size === 0) return [...changes];

  return changes.filter((change, index) => {
    const elementId = getChangeAffectedElementId(change);
    const slot = getOverlapSlotKey(change);
    if (!elementId || !slot) return true;
    const key = `${elementId}:${slot}:${change.type}`;
    return lastIndexByKey.get(key) === index;
  });
};

export const dedupeCategoryEnrichChangesWithinAction = (
  changes: ProjectChange[],
): ProjectChange[] => {
  const lastIndexByElement = new Map<string, number>();
  for (let i = 0; i < changes.length; i++) {
    const change = changes[i];
    if (!change || change.type !== ProjectChangeType.ElementCategoryEnrich) {
      continue;
    }
    lastIndexByElement.set(change.elementId, i);
  }
  if (lastIndexByElement.size === 0) return changes;

  return changes.filter((change, index) => {
    if (change.type !== ProjectChangeType.ElementCategoryEnrich) return true;
    return lastIndexByElement.get(change.elementId) === index;
  });
};

/**
 * Builds the set of `${elementId}:${overlapSlotKey}` strings touched by {@link changes}.
 * Used by {@link subtractOverlappingChangesFromProposalActions} to know which previously
 * stored changes are now redundant.
 */
const buildOverlapKeySet = (changes: readonly ProjectChange[]): Set<string> => {
  const keys = new Set<string>();
  for (const change of changes) {
    const elementId = getChangeAffectedElementId(change);
    if (!elementId) continue;
    for (const slot of getOverlapSlotKeys(change)) {
      keys.add(`${elementId}:${slot}`);
    }
  }
  return keys;
};

/**
 * True when the change clears a user count/override so fallback/inherit would apply.
 */
export const isProposalCountClearChange = (change: ProjectChange): boolean => {
  switch (change.type) {
    case ProjectChangeType.ElementQuantityCount:
    case ProjectChangeType.ElementPropertyCount:
      return change.count === undefined;
    case ProjectChangeType.ElementQuantityValue:
      return change.overrideValue === undefined;
    default:
      return false;
  }
};

/**
 * True when a stored proposal change is a user-authored value/expression set.
 * Clears and pipeline companions ({@link ProjectChangeType.ElementQuantityResolved},
 * {@link ProjectChangeType.ElementPropertyResolved}, fallback-count rows) do not
 * own the slot for {@link dropProposalCountClearChanges}.
 */
export const isProposalOwnedSlotChange = (change: ProjectChange): boolean => {
  if (isProposalCountClearChange(change)) return false;
  switch (change.type) {
    case ProjectChangeType.ElementQuantityCount:
    case ProjectChangeType.ElementPropertyCount:
    case ProjectChangeType.ElementQuantityValue:
    case ProjectChangeType.ElementQuantityExpression:
    case ProjectChangeType.ElementPropertyExpression:
    case ProjectChangeType.ElementQuantityFallbackExpression:
      return true;
    case ProjectChangeType.ElementPropertyValue:
      return change.overrideValue !== undefined;
    default:
      return false;
  }
};

/**
 * Overlap keys (`${elementId}:${slot}`) for user-authored value/expression sets
 * currently stored on a proposal's actions. Used by
 * {@link dropProposalCountClearChanges} so a clear reverts those sets to baseline
 * without treating a stored inherit-clear as ownership.
 */
export const getProposalActionOverlapKeySet = (
  version: Baseline,
  proposalId: ProposalId,
): Set<string> => {
  const proposal = getItemById(version.proposals, proposalId, false);
  if (!proposal?.actionIds?.length) return new Set();

  const proposalActionIds = new Set(proposal.actionIds);
  const changes: ProjectChange[] = [];
  for (const action of version.actions ?? []) {
    if (!proposalActionIds.has(action.id)) continue;
    for (const change of action.changes) {
      if (!isProposalOwnedSlotChange(change)) continue;
      changes.push(change);
    }
  }
  return buildOverlapKeySet(changes);
};

/** Element ids removed by delete/remove changes in an incoming batch. */
const buildDeletedElementIds = (changes: ProjectChange[]): Set<string> => {
  const ids = new Set<string>();
  for (const change of changes) {
    if (
      change.type !== ProjectChangeType.ElementSoftDelete &&
      change.type !== ProjectChangeType.ElementRemove
    ) {
      continue;
    }
    const elementId = getChangeAffectedElementId(change);
    if (elementId) ids.add(elementId);
  }
  return ids;
};

/**
 * Recipe-applied child id prefix produced by {@link assignDeterministicRecipeIds};
 * see {@link applyRecipe}. Used to match proposal-only recipe children that don't exist in baseline.
 */
const RECIPE_CHILD_ID_INFIX = ':r:';

/**
 * Collects element ids whose subtree must be wiped from prior proposal actions
 * because the incoming batch contains a structural recipe change on that element
 * ({@link ProjectChangeType.ElementRecipeApply}, detach, or clear).
 *
 * Returns both the descendant ids found in the **baseline** version subtree and the
 * deterministic recipe-child prefix `${parentId}:r:` so callers can also match
 * proposal-only children that a previous proposal-scoped recipe apply produced (DEV-3293).
 */
const buildRecipeSwappedChildMatcher = (
  version: Baseline,
  changes: ProjectChange[],
): { descendantIds: Set<string>; childIdPrefixes: string[] } => {
  const descendantIds = new Set<string>();
  const childIdPrefixes: string[] = [];
  for (const change of changes) {
    if (
      change.type !== ProjectChangeType.ElementRecipeApply &&
      change.type !== ProjectChangeType.ElementRecipeDetach &&
      change.type !== ProjectChangeType.ElementRecipeClear
    ) {
      continue;
    }
    const elementId = getChangeAffectedElementId(change);
    if (!elementId) continue;
    const parent = getElementById(version, elementId, false);
    if (parent && !isBuildingVersionElement(parent)) {
      // flattenElements includes the parent itself; we only want descendants.
      for (const node of flattenElements(parent)) {
        if (node.id !== elementId) descendantIds.add(node.id);
      }
    }
    childIdPrefixes.push(`${elementId}${RECIPE_CHILD_ID_INFIX}`);
  }
  return { descendantIds, childIdPrefixes };
};

/**
 * Whether a stored change targets a child element of an element that the incoming batch
 * is swapping the recipe on. Either a baseline descendant id or a deterministic
 * recipe-applied child id ({@link buildRecipeSwappedChildMatcher}).
 */
const isRecipeSwappedChildChange = (
  change: ProjectChange,
  descendantIds: ReadonlySet<string>,
  childIdPrefixes: readonly string[],
): boolean => {
  const elementId = getChangeAffectedElementId(change);
  if (!elementId) return false;
  if (descendantIds.has(elementId)) return true;
  return childIdPrefixes.some((prefix) => elementId.startsWith(prefix));
};

/**
 * Removes empty {@link Action} entries from {@link Baseline.actions} and prunes the
 * dropped action ids from every {@link IProposal.actionIds}. Returns the same reference
 * when nothing changes.
 */
export const compactActions = (version: Baseline): Baseline => {
  const actions = version.actions;
  if (!actions?.length) return version;

  const droppedIds = new Set<string>();
  const nextActions: Action[] = [];
  for (const action of actions) {
    if (action.changes.length === 0) {
      droppedIds.add(action.id);
      continue;
    }
    nextActions.push(action);
  }

  if (droppedIds.size === 0) return version;

  const proposals = version.proposals?.map((proposal) => {
    // Persisted rows may omit actionIds before full migration / repair
    const ids = proposal.actionIds ?? [];
    const filtered = ids.filter((id) => !droppedIds.has(id));
    if (filtered.length === ids.length && proposal.actionIds !== undefined) {
      return proposal;
    }
    return { ...proposal, actionIds: filtered };
  });

  return {
    ...version,
    actions: nextActions,
    ...(proposals ? { proposals } : {}),
  };
};

/**
 * Drops any `actionIds` entry on the version's `proposals` that does not resolve to an
 * action in `version.actions`. Useful after a caller has mutated `version.actions`
 * directly (e.g. user-driven action delete from the UI) to keep the invariant that every
 * referenced action exists. Returns the same reference when nothing changes.
 */
export const pruneDanglingActionReferences = (version: Baseline): Baseline => {
  const proposals = version.proposals;
  if (!proposals?.length) return version;

  const validIds = new Set((version.actions ?? []).map((a) => a.id));

  let mutated = false;
  const nextProposals = proposals.map((proposal) => {
    const ids = proposal.actionIds ?? [];
    const filtered = ids.filter((id) => validIds.has(id));
    if (filtered.length === ids.length && proposal.actionIds !== undefined) {
      return proposal;
    }
    mutated = true;
    return { ...proposal, actionIds: filtered };
  });

  if (!mutated) return version;
  return { ...version, proposals: nextProposals };
};

/**
 * Drops changes whose primary `elementId` is in {@link removedElementIds}, and recursively
 * filters {@link ProjectChangeType.ProposalAppendAction} inner `changes` (append payloads
 * have no top-level `elementId`, so a flat filter would leave stale inner edits behind).
 *
 * @param removedElementIds - Subtree ids already removed from the version tree
 * @param changes - Flat list of project changes (e.g. one action's `changes` array)
 * @returns A new list with removals applied; may be shorter or contain cloned append changes
 */
export const stripProjectChangesForRemovedElements = <T extends ProjectChange>(
  removedElementIds: ReadonlySet<string>,
  changes: readonly T[],
): T[] => {
  if (removedElementIds.size === 0 || changes.length === 0) {
    return [...changes];
  }
  const out: T[] = [];
  for (const change of changes) {
    const next = filterProjectChangeForRemovedElements(
      removedElementIds,
      change,
    );
    if (next) out.push(next as T);
  }
  return out;
};

/**
 * Applies {@link stripProjectChangesForRemovedElements} to a single change, returning
 * `undefined` when the change should be removed entirely.
 */
const filterProjectChangeForRemovedElements = (
  removedElementIds: ReadonlySet<string>,
  change: ProjectChange,
): ProjectChange | undefined => {
  if (change.type === ProjectChangeType.ProposalAppendAction) {
    const inner = stripProjectChangesForRemovedElements(
      removedElementIds,
      change.changes,
    );
    if (inner.length === 0) return undefined;
    if (
      inner.length === change.changes.length &&
      inner.every((c, i) => c === change.changes[i])
    ) {
      return change;
    }
    return { ...change, changes: inner };
  }
  const elementId = getChangeAffectedElementId(change);
  if (elementId && removedElementIds.has(elementId)) return undefined;
  return change;
};

/**
 * Strips changes referencing any id in {@link removedElementIds} from every action on the
 * version, then runs {@link compactActions} so emptied actions disappear from
 * `version.actions` and from any `proposals[*].actionIds`.
 *
 * Callers are responsible for collecting descendant ids of removed elements before calling
 * (a removed parent implicitly removes its children).
 */
export const cleanupActionsForRemovedElements = (
  version: Baseline,
  removedElementIds: ReadonlySet<string>,
): Baseline => {
  const actions = version.actions;
  if (!actions?.length || removedElementIds.size === 0) return version;

  let mutated = false;
  const nextActions: Action[] = actions.map((action) => {
    const nextChanges = stripProjectChangesForRemovedElements(
      removedElementIds,
      action.changes,
    );
    if (
      nextChanges.length === action.changes.length &&
      nextChanges.every((c, i) => c === action.changes[i])
    ) {
      return action;
    }
    mutated = true;
    return { ...action, changes: nextChanges };
  });

  if (!mutated) return version;

  return compactActions({ ...version, actions: nextActions });
};

/**
 * Removes any change in actions referenced by the given proposal that targets the same
 * `elementId + slot` as one of {@link newChanges}. Empties are then cleaned via
 * {@link compactActions} (which also unlinks them from the proposal's `actionIds`).
 *
 * Assumes the invariant that an action is referenced by at most one proposal — the same
 * assumption {@link applyProjectChangesToProposal} relies on when creating a fresh action per
 * write.
 */
export const subtractOverlappingChangesFromProposalActions = (
  version: Baseline,
  proposalId: ProposalId,
  newChanges: ProjectChange[],
): Baseline => {
  const actions = version.actions;
  const proposals = version.proposals;
  if (!actions?.length || !proposals?.length) return version;

  const proposal = getItemById(proposals, proposalId, false);
  const proposalActionIdList = proposal?.actionIds ?? [];
  if (!proposal || proposalActionIdList.length === 0) return version;

  const overlapKeys = buildOverlapKeySet(newChanges);
  const deletedElementIds = buildDeletedElementIds(newChanges);
  const staleEnrichResetElements =
    getElementsWithCategoryStructureDriverWithoutEnrich(newChanges, version);
  const incomingEnrichElements =
    getIncomingCategoryEnrichElementIds(newChanges);
  const {
    descendantIds: recipeSwappedChildIds,
    childIdPrefixes: recipeChildIdPrefixes,
  } = buildRecipeSwappedChildMatcher(version, newChanges);
  if (
    overlapKeys.size === 0 &&
    deletedElementIds.size === 0 &&
    staleEnrichResetElements.size === 0 &&
    incomingEnrichElements.size === 0 &&
    recipeSwappedChildIds.size === 0 &&
    recipeChildIdPrefixes.length === 0
  ) {
    return version;
  }

  const proposalActionIds = new Set<string>(proposalActionIdList);
  let mutated = false;

  const nextActions: Action[] = actions.map((action) => {
    if (!proposalActionIds.has(action.id)) return action;
    const nextChanges: ProjectChange[] = [];
    for (const change of action.changes) {
      // Inactive changes never apply and never claim slots — keep untouched (DEV-3640)
      if (change.inactive) {
        nextChanges.push(change);
        continue;
      }
      const elementId = getChangeAffectedElementId(change);
      if (!elementId) {
        nextChanges.push(change);
        continue;
      }
      if (deletedElementIds.has(elementId)) continue;
      // Recipe swap on a parent invalidates every prior edit to its children (DEV-3293).
      if (
        isRecipeSwappedChildChange(
          change,
          recipeSwappedChildIds,
          recipeChildIdPrefixes,
        )
      ) {
        continue;
      }
      if (
        (staleEnrichResetElements.has(elementId) ||
          incomingEnrichElements.has(elementId)) &&
        change.type === ProjectChangeType.ElementCategoryEnrich
      ) {
        continue;
      }
      if (isGranularProductElementOverrideChange(change)) {
        const slots = getOverlapSlotKeys(change);
        if (slots.some((slot) => overlapKeys.has(`${elementId}:${slot}`))) {
          continue;
        }
        nextChanges.push(change);
        continue;
      }
      const slot = getOverlapSlotKey(change);
      if (!slot || !overlapKeys.has(`${elementId}:${slot}`)) {
        nextChanges.push(change);
      }
    }
    if (
      nextChanges.length === action.changes.length &&
      nextChanges.every((change, index) => change === action.changes[index])
    ) {
      return action;
    }
    mutated = true;
    return { ...action, changes: nextChanges };
  });

  if (!mutated) return version;
  return compactActions({ ...version, actions: nextActions });
};

/**
 * Builds a map of property names removed by category for each element id from incoming
 * {@link ProjectChangeType.ElementCategoryEnrich} payloads.
 */
const buildCategoryRemovedPropertyNamesByElement = (
  incomingChanges: readonly ProjectChange[],
): Map<string, Set<string>> => {
  const byElement = new Map<string, Set<string>>();
  for (const change of incomingChanges) {
    if (change.type !== ProjectChangeType.ElementCategoryEnrich) continue;
    const set = byElement.get(change.elementId) ?? new Set<string>();
    for (const name of change.removePropertyNames) set.add(name);
    byElement.set(change.elementId, set);
  }
  return byElement;
};

/**
 * True when {@link change} targets an element property whose name is removed by category
 * in the same incoming batch.
 */
const changeTargetsCategoryRemovedProperty = (
  change: ProjectChange,
  removedByElement: ReadonlyMap<string, Set<string>>,
): boolean => {
  const elementId = getChangeAffectedElementId(change);
  if (!elementId) return false;
  const removed = removedByElement.get(elementId);
  if (!removed?.size) return false;
  if (!('propertyName' in change)) return false;
  const name = (change as { propertyName?: unknown }).propertyName;
  if (typeof name !== 'string') return false;
  return removed.has(name);
};

/**
 * Collects property names that were added by {@link ProjectChangeType.ElementCategoryEnrich}
 * on elements listed in {@link staleEnrichElementIds}.
 */
const collectEnrichAddedPropertyNamesByElement = (
  actions: readonly Action[],
  proposalActionIds: ReadonlySet<string>,
  staleEnrichElementIds: ReadonlySet<string>,
): Map<string, Set<string>> => {
  const byElement = new Map<string, Set<string>>();
  for (const action of actions) {
    if (!proposalActionIds.has(action.id)) continue;
    for (const change of action.changes) {
      if (change.type !== ProjectChangeType.ElementCategoryEnrich) continue;
      if (!staleEnrichElementIds.has(change.elementId)) continue;
      if (change.addProperties.length === 0) continue;
      const set = byElement.get(change.elementId) ?? new Set<string>();
      for (const property of change.addProperties) set.add(property.name);
      byElement.set(change.elementId, set);
    }
  }
  return byElement;
};

/** Property edits that can append {@link ProjectChangeType.ElementCategoryEnrich} via the client pipeline. */
const CATEGORY_PROPERTY_EDIT_CHANGE_TYPES = new Set<ProjectChangeType>([
  ProjectChangeType.ElementPropertyResolved,
  ProjectChangeType.ElementPropertyExpression,
  ProjectChangeType.ElementPropertyValue,
  ProjectChangeType.ElementPropertyCount,
  ProjectChangeType.ElementPropertyFallbackCount,
]);

/**
 * Element ids where the incoming batch includes a category property edit that would not
 * alter the baseline element tree (user reverted a selector to the baseline value).
 */
const getElementsWithNoOpCategoryPropertyIncomingEdits = (
  version: Baseline,
  incomingChanges: readonly ProjectChange[],
): Set<string> => {
  const ids = new Set<string>();
  for (const change of incomingChanges) {
    if (!CATEGORY_PROPERTY_EDIT_CHANGE_TYPES.has(change.type)) continue;
    const elementId = getChangeAffectedElementId(change);
    if (!elementId) continue;
    const element = getElementById(version, elementId, false);
    if (!element || !isElement(element)) continue;
    if (getElementCategoryId(element) === ElementCategoryID.None) continue;
    if (projectChangeToElementPartial(element, change) !== undefined) continue;
    ids.add(elementId);
  }
  return ids;
};

/**
 * Removes {@link ProjectChangeType.ElementCategoryEnrich} rows and proposal edits to
 * properties those enrich rows added for the given {@link elementIds} on one proposal.
 */
const stripCategoryEnrichFromProposalActionsForElements = (
  version: Baseline,
  proposalId: ProposalId,
  elementIds: ReadonlySet<string>,
): Baseline => {
  if (elementIds.size === 0) return version;

  const actions = version.actions;
  const proposals = version.proposals;
  if (!actions?.length || !proposals?.length) return version;

  const proposal = getItemById(proposals, proposalId, false);
  const proposalActionIdList = proposal?.actionIds ?? [];
  if (!proposal || proposalActionIdList.length === 0) return version;

  const proposalActionIds = new Set<string>(proposalActionIdList);
  const addedNamesByElement = collectEnrichAddedPropertyNamesByElement(
    actions,
    proposalActionIds,
    elementIds,
  );

  let mutated = false;
  const nextActions: Action[] = actions.map((action) => {
    if (!proposalActionIds.has(action.id)) return action;

    const nextChanges = action.changes.filter((change) => {
      const elementId = getChangeAffectedElementId(change);
      if (
        elementId &&
        elementIds.has(elementId) &&
        change.type === ProjectChangeType.ElementCategoryEnrich
      ) {
        return false;
      }
      if (changeTargetsCategoryRemovedProperty(change, addedNamesByElement)) {
        return false;
      }
      return true;
    });

    if (nextChanges.length === action.changes.length) return action;
    mutated = true;
    return { ...action, changes: nextChanges };
  });

  if (!mutated) return version;
  return { ...version, actions: nextActions };
};

/**
 * Removes proposal {@link ProjectChangeType.ElementCategoryEnrich} rows (and edits to
 * properties those rows added) when the user reverts a category property to the baseline
 * value. Covers product swaps driven by selectors such as concrete `quality` (C28 → C30)
 * where the incoming batch is a no-op property edit and does not emit a replacement enrich.
 */
export const stripObsoleteCategoryEnrichOnBaselinePropertyRevert = (
  version: Baseline,
  proposalId: ProposalId,
  incomingChanges: readonly ProjectChange[],
): Baseline => {
  const revertElementIds = getElementsWithNoOpCategoryPropertyIncomingEdits(
    version,
    incomingChanges,
  );
  const stripped = stripCategoryEnrichFromProposalActionsForElements(
    version,
    proposalId,
    revertElementIds,
  );
  if (stripped === version) return version;
  return compactActions(stripped);
};

/**
 * Drops incoming {@link ProjectChangeType.ElementCategoryEnrich} rows on elements where the
 * batch also contains a baseline-no-op category property edit (revert path). The client still
 * emits enrich from the proposal-resolved element even though only the property row is needed
 * to clear stale proposal actions.
 */
export const dropIncomingCategoryEnrichOnBaselinePropertyRevert = (
  version: Baseline,
  incomingChanges: readonly ProjectChange[],
): ProjectChange[] => {
  const revertElementIds = getElementsWithNoOpCategoryPropertyIncomingEdits(
    version,
    incomingChanges,
  );
  if (revertElementIds.size === 0) return [...incomingChanges];

  return incomingChanges.filter((change) => {
    if (change.type !== ProjectChangeType.ElementCategoryEnrich) return true;
    const elementId = getChangeAffectedElementId(change);
    if (!elementId) return true;
    return !revertElementIds.has(elementId);
  });
};

/**
 * Whether any stored proposal change still needs to persist when the resolved tree
 * deep-equals baseline (e.g. stale `count.value` on a `gfa` row — DEV-3313).
 */
const proposalHasPersistableChangesWhenResolvedMatchesBaseline = (
  version: Baseline,
  proposalId: ProposalId,
): boolean => {
  const proposal = getItemById(version.proposals, proposalId, false);
  const proposalActionIdList = proposal?.actionIds ?? [];
  if (!proposalActionIdList.length) return false;

  const actions = version.actions ?? [];
  for (const actionId of proposalActionIdList) {
    const action = actions.find((candidate) => candidate.id === actionId);
    if (!action) continue;
    for (const change of action.changes) {
      const elementId = getChangeAffectedElementId(change);
      if (!elementId) return true;
      const element = getElementById(version, elementId, false);
      if (!element || isBuildingVersionElement(element)) continue;
      if (
        change.type === ProjectChangeType.ElementQuantityValue &&
        mustPersistStaleExpressionQuantityValueOverride(element, change)
      ) {
        return true;
      }
      if (projectChangeToElementPartial(element, change) !== undefined) {
        return true;
      }
    }
  }
  return false;
};

/**
 * When the proposal's resolved element tree matches baseline, unlinks all of its actions.
 * Catches stale rows that survive per-change no-op filtering (e.g. orphaned category enrich).
 */
export const compactProposalActionsWhenResolvedMatchesBaseline = (
  version: Baseline,
  proposalId: ProposalId,
): Baseline => {
  const proposal = getItemById(version.proposals, proposalId, false);
  const proposalActionIdList = proposal?.actionIds ?? [];
  if (!proposal || proposalActionIdList.length === 0) return version;

  const { version: resolved } = resolveVersionForProposal(version, proposal);
  if (!compare.equal.deep(resolved.elements, version.elements)) return version;
  if (
    proposalHasPersistableChangesWhenResolvedMatchesBaseline(
      version,
      proposalId,
    )
  ) {
    return version;
  }

  const proposalActionIds = new Set<string>(proposalActionIdList);
  const actions = version.actions;
  if (!actions?.length) return version;

  const nextActions = actions.filter((a) => !proposalActionIds.has(a.id));
  const proposals = version.proposals;
  if (!proposals?.length) return version;

  const nextProposals = proposals.map((p) =>
    p.id === proposalId ? { ...p, actionIds: [] as string[] } : p,
  );

  return compactActions({
    ...version,
    actions: nextActions,
    proposals: nextProposals,
  });
};

/**
 * When the incoming batch changes a category-driving property without a new enrich,
 * removes stale {@link ProjectChangeType.ElementCategoryEnrich} rows and proposal edits
 * to properties those enrich rows had added.
 */
export const stripStaleCategoryEnrichFromProposalActions = (
  version: Baseline,
  proposalId: ProposalId,
  incomingChanges: ProjectChange[],
): Baseline => {
  const staleEnrichElementIds =
    getElementsWithCategoryStructureDriverWithoutEnrich(
      incomingChanges,
      version,
    );
  return stripCategoryEnrichFromProposalActionsForElements(
    version,
    proposalId,
    staleEnrichElementIds,
  );
};

/**
 * Removes stored proposal edits that target properties dropped by an incoming
 * {@link ProjectChangeType.ElementCategoryEnrich}. Baseline `version.elements` may still
 * list those names, so {@link dropNoOpChangesAgainstBaseline} alone cannot scrub them.
 *
 * @param version - Version whose `actions` / `proposals` may be updated
 * @param proposalId - Proposal receiving the append
 * @param incomingChanges - Condensed batch (typically includes enrich when category shrinks)
 * @returns Same reference when nothing stripped; otherwise updated version (not compacted)
 */
export const stripCategoryRemovedPropertyEditsFromProposalActions = (
  version: Baseline,
  proposalId: ProposalId,
  incomingChanges: ProjectChange[],
): Baseline => {
  const removedByElement =
    buildCategoryRemovedPropertyNamesByElement(incomingChanges);
  if (removedByElement.size === 0) return version;

  const actions = version.actions;
  const proposals = version.proposals;
  if (!actions?.length || !proposals?.length) return version;

  const proposal = getItemById(proposals, proposalId, false);
  const proposalActionIdList = proposal?.actionIds ?? [];
  if (!proposal || proposalActionIdList.length === 0) return version;

  const proposalActionIds = new Set<string>(proposalActionIdList);
  let mutated = false;

  const nextActions: Action[] = actions.map((action) => {
    if (!proposalActionIds.has(action.id)) return action;
    const nextChanges = action.changes.filter(
      (c) => !changeTargetsCategoryRemovedProperty(c, removedByElement),
    );
    if (nextChanges.length === action.changes.length) return action;
    mutated = true;
    return { ...action, changes: nextChanges };
  });

  if (!mutated) return version;
  return { ...version, actions: nextActions };
};

/**
 * For each {@link IProposal}, walks {@link IProposal.actionIds} from **newest to oldest**
 * (last id first). Any change whose {@link getOverlapSlotKey} was already claimed by a
 * newer action on that proposal is dropped — older edits on the same element+slot are
 * removed so only the latest action keeps that slot.
 *
 * Then {@link compactActions} removes actions emptied by the prune.
 *
 * @param version - Building version carrying `actions` / `proposals`
 * @returns Same reference when nothing pruned; otherwise updated version
 */
export const pruneSubsumedOverlapChangesInProposalActions = (
  version: Baseline,
): Baseline => {
  const actions = version.actions;
  const proposals = version.proposals;
  if (!actions?.length || !proposals?.length) return version;

  const actionById = new Map(actions.map((a) => [a.id, a]));
  const nextChangesByActionId = new Map<string, ProjectChange[]>();

  let mutated = false;

  for (const proposal of proposals) {
    const claimedSlots = new Set<string>();
    const deletedElementIds = new Set<string>();
    const ids = proposal.actionIds ?? [];
    for (let i = ids.length - 1; i >= 0; i--) {
      const actionId = ids[i];
      if (actionId === undefined) continue;
      const action = actionById.get(actionId);
      if (!action) continue;

      const raw = nextChangesByActionId.get(actionId) ?? action.changes;
      // Inactive changes never apply, claim no slots, and must survive the prune
      // untouched (DEV-3640) — re-appended after the active rows below.
      const inactiveChanges = raw.filter((change) => change.inactive);
      const activeRaw = inactiveChanges.length
        ? raw.filter((change) => !change.inactive)
        : raw;
      const current = dedupeSameSlotSameTypeChangesWithinAction(
        dedupeRedundantResolvedWhenOverridePresentWithinAction(
          dedupeCategoryEnrichChangesWithinAction(activeRaw),
        ),
      );
      if (current.length !== activeRaw.length) mutated = true;

      const filtered = current.filter((change) => {
        const elementId = getChangeAffectedElementId(change);
        if (!elementId) return true;
        if (deletedElementIds.has(elementId)) return false;
        if (isGranularProductElementOverrideChange(change)) {
          return getOverlapSlotKeys(change).some(
            (slot) => !claimedSlots.has(`${elementId}:${slot}`),
          );
        }
        const slot = getOverlapSlotKey(change);
        if (!slot) return true;
        const key = `${elementId}:${slot}`;
        return !claimedSlots.has(key);
      });

      const normalized: ProjectChange[] = [];
      for (const change of filtered) {
        const elementId = getChangeAffectedElementId(change);
        if (!elementId) {
          normalized.push(change);
          continue;
        }
        normalized.push(change);
      }

      if (
        normalized.length !== current.length ||
        normalized.some((change, index) => change !== filtered[index])
      ) {
        mutated = true;
      }

      for (const change of normalized) {
        const elementId = getChangeAffectedElementId(change);
        if (!elementId) continue;
        if (
          change.type === ProjectChangeType.ElementSoftDelete ||
          change.type === ProjectChangeType.ElementRemove
        ) {
          deletedElementIds.add(elementId);
        }
        for (const slot of getOverlapSlotKeys(change)) {
          claimedSlots.add(`${elementId}:${slot}`);
        }
      }

      nextChangesByActionId.set(actionId, [...normalized, ...inactiveChanges]);
    }
  }

  if (!mutated) return version;

  const nextActions = actions.map((a) => ({
    ...a,
    changes: nextChangesByActionId.get(a.id) ?? a.changes,
  }));

  return compactActions({ ...version, actions: nextActions });
};

/**
 * Runs {@link pruneSubsumedOverlapChangesInProposalActions} on every building version that
 * has {@link IProposal} entries.
 *
 * @param project - Project to normalize
 * @returns Updated project when any version pruned; otherwise the same reference
 */
export const pruneSubsumedOverlapChangesInProject = (
  project: Project,
): Project => {
  const versionIds: string[] = [];
  for (const v of project.baselines) {
    if (v?.proposals?.length) versionIds.push(v.id);
  }

  let next = project;
  for (const id of versionIds) {
    const version = getBuildingVersionById(next, id, false);
    if (!version?.proposals?.length) continue;
    const pruned = pruneSubsumedOverlapChangesInProposalActions(version);
    if (pruned === version) continue;
    next = updateBuildingVersionProperties(next, {
      id,
      actions: pruned.actions,
      proposals: pruned.proposals,
    });
  }

  return next === project ? project : next;
};

/**
 * Element ids that only exist in some proposal's resolved view of {@link version} — i.e. nodes
 * introduced by proposal-scoped structural changes such as {@link ProjectChangeType.ElementRecipeApply}
 * (DEV-3293). Used by {@link dropNoOpChangesAgainstBaseline} so that property/quantity edits
 * targeting those nodes are not mistaken for stale references.
 *
 * Cached by version reference because resolving every proposal is expensive on hot paths
 * (`applyBatchChanges` post-pass).
 */
export const getProposalOnlyElementIds = (
  version: Baseline,
): ReadonlySet<string> => {
  return cacheFactory(
    () => {
      const proposals = version.proposals;
      if (!proposals?.length) return new Set<string>();

      const baselineIds = new Set<string>();
      forEachElement(version, (element) => baselineIds.add(element.id));

      const ids = new Set<string>();
      for (const proposal of proposals) {
        if (!proposal.actionIds?.length) continue;
        const { version: resolved } = resolveVersionForProposal(
          version,
          proposal,
        );
        if (resolved === version) continue;
        forEachElement(resolved, (element) => {
          if (!baselineIds.has(element.id)) ids.add(element.id);
        });
      }
      return ids;
    },
    `getProposalOnlyElementIds[${version.id}]`,
    [version],
  );
};

/**
 * Tree used for the partial no-op check on a single incoming change.
 * Compare against baseline first; only fall back to proposal-resolved when the change is a
 * no-op on baseline but would still alter the resolved row (DEV-3313 recipe children, enriched
 * baseline `count.value`, inherited `gfa`, etc.). Re-typing a value that matches baseline still
 * uses baseline so overlapping proposal overrides can be subtracted and dropped.
 */
const getElementSnapshotForNoOpCheck = (
  baselineVersion: Baseline,
  elementId: string,
  change: ProjectChange,
  resolvedVersion?: Baseline,
): OneOfElements | undefined => {
  const baseline = getElementById(baselineVersion, elementId, false);
  if (!baseline || isBuildingVersionElement(baseline)) {
    return baseline as OneOfElements | undefined;
  }
  if (!resolvedVersion) return baseline;

  const resolved = getElementById(resolvedVersion, elementId, false);
  if (!resolved || isBuildingVersionElement(resolved)) return baseline;

  if (projectChangeToElementPartial(baseline, change) !== undefined) {
    return baseline;
  }
  return resolved;
};

/** Narrowed quantity override change types used for formula-row re-save detection (DEV-3313). */
type FormulaQuantityOverrideChange =
  | ProjectChangeMap[ProjectChangeType.ElementQuantityValue]
  | ProjectChangeMap[ProjectChangeType.ElementQuantityResolved];

const asFormulaQuantityOverrideChange = (
  change: ProjectChange | undefined,
): FormulaQuantityOverrideChange | undefined => {
  if (!change) return undefined;
  if (change.type === ProjectChangeType.ElementQuantityValue) return change;
  if (change.type === ProjectChangeType.ElementQuantityResolved) return change;
  return undefined;
};

const getFormulaQuantityOverrideValue = (
  change: FormulaQuantityOverrideChange,
): number | undefined =>
  change.type === ProjectChangeType.ElementQuantityValue
    ? change.overrideValue
    : change.value;

/**
 * Whether the user re-submitted the same formula-row quantity override already stored on the
 * proposal while the EL cell still shows the inherited resolved value (DEV-3313).
 */
export const isRedundantFormulaQuantityOverrideResave = (
  version: Baseline,
  proposalId: ProposalId,
  changes: readonly ProjectChange[],
  displayHints?: ReadonlyArray<DropNoOpQuantityDisplayHint>,
): boolean => {
  if (changes.length !== 1) return false;
  const change = asFormulaQuantityOverrideChange(changes[0]);
  if (!change) return false;
  const overrideValue = getFormulaQuantityOverrideValue(change);

  const displayHint = displayHints?.find(
    (hint) =>
      hint.elementId === change.elementId &&
      hint.quantityName === change.quantityName,
  );
  if (
    displayHint?.displayResolved === undefined ||
    displayHint.displayResolved === overrideValue
  ) {
    return false;
  }

  const proposal = version.proposals?.find((p) => p.id === proposalId);
  if (!proposal?.actionIds?.length) return false;

  for (const actionId of proposal.actionIds) {
    const action = version.actions?.find(
      (candidate) => candidate.id === actionId,
    );
    if (!action) continue;
    for (const candidate of action.changes) {
      const prior = asFormulaQuantityOverrideChange(candidate);
      if (!prior) continue;
      if (
        prior.elementId === change.elementId &&
        prior.quantityName === change.quantityName &&
        getFormulaQuantityOverrideValue(prior) === overrideValue
      ) {
        return true;
      }
    }
  }
  return false;
};

/**
 * EL quantity cell state when {@link dropNoOpChangesAgainstBaseline} runs in the client.
 * Used when enrich caches disagree with what the user sees (DEV-3313).
 */
export type DropNoOpQuantityDisplayHint = {
  elementId: string;
  quantityName: ElementQuantityName;
  /** Resolved value shown in the quantity input when the edit was made. */
  displayResolved?: number;
};

/**
 * Drops {@link isProposalCountClearChange} rows whose slot is already owned by a
 * proposal action ({@link getProposalActionOverlapKeySet}). Those clears revert
 * the slot to baseline. Clears of baseline values — and re-clears of an already
 * stored inherit — are kept so inherit-from-parent stays stored.
 *
 * Overlap subtract must run on the unfiltered batch first so prior overrides are removed.
 */
export const dropProposalCountClearChanges = (
  changes: readonly ProjectChange[],
  proposalOverlapKeys: ReadonlySet<string> = new Set(),
): ProjectChange[] =>
  changes.filter((change) => {
    if (!isProposalCountClearChange(change)) return true;
    const elementId = getChangeAffectedElementId(change);
    if (!elementId) return true;
    const belongsToProposalAction = getOverlapSlotKeys(change).some((slot) =>
      proposalOverlapKeys.has(`${elementId}:${slot}`),
    );
    return !belongsToProposalAction;
  });

/**
 * Filters out changes that would not alter the baseline element snapshot.
 *
 * The baseline is the raw `version.elements` tree (no proposal actions applied). Reuses
 * {@link projectChangeToElementPartial}: when it returns `undefined`, the change is a no-op
 * against the current element and is dropped.
 *
 * When {@link resolvedVersion} is set and a change is a no-op on baseline but not on that
 * resolved tree, the resolved snapshot is used so quantity edits are not dropped (DEV-3313).
 *
 * Version-scoped changes (no `elementId`) pass through. When an `elementId` is set but the
 * node is missing from the tree, the change is dropped except for
 * {@link ProjectChangeType.ElementActivate} (same rationale as proposal validation: the
 * element may be introduced earlier in the same action batch) and proposal-only structural
 * additions in {@link allowedProposalOnlyIds} (e.g. recipe children added by a proposal-scoped
 * {@link ProjectChangeType.ElementRecipeApply}, see DEV-3293) — those bypass the partial no-op check
 * because their baseline counterpart by definition doesn't exist.
 */
export const dropNoOpChangesAgainstBaseline = (
  version: Baseline,
  changes: ProjectChange[],
  allowedProposalOnlyIds?: ReadonlySet<string>,
  resolvedVersion?: Baseline,
  displayHints?: ReadonlyArray<DropNoOpQuantityDisplayHint>,
): ProjectChange[] => {
  if (changes.length === 0) return changes;

  const result: ProjectChange[] = [];
  for (const change of changes) {
    const elementId = getChangeAffectedElementId(change);
    if (!elementId) {
      result.push(change);
      continue;
    }

    // Formula/inherit quantity rows (gfa, …): keep value overrides even when
    // projectChangeToElementPartial would drop them (DEV-3313). Never fall through to
    // the generic partial check below — that path drops stale-formula overrides.
    if (change.type === ProjectChangeType.ElementQuantityValue) {
      const resolved = resolvedVersion
        ? getElementById(resolvedVersion, elementId, false)
        : undefined;
      const baseline = getElementById(version, elementId, false);
      const existsOnProposalTree = (tree: OneOfElements | undefined): boolean =>
        Boolean(tree && !isBuildingVersionElement(tree));

      const displayHint = displayHints?.find(
        (hint) =>
          hint.elementId === elementId &&
          hint.quantityName === change.quantityName,
      );
      // Only trust the EL display hint when the target exists on baseline or resolved;
      // otherwise we would append actions that fail proposal validation (DEV-3313).
      if (
        displayHint?.displayResolved !== undefined &&
        displayHint.displayResolved !== change.overrideValue &&
        (existsOnProposalTree(baseline) ||
          existsOnProposalTree(resolved) ||
          allowedProposalOnlyIds?.has(elementId))
      ) {
        const proposalTree = resolved ?? baseline;
        const qty =
          proposalTree && !isBuildingVersionElement(proposalTree)
            ? getElementQuantityRecord(proposalTree)[change.quantityName]
            : undefined;
        const count = isElementQuantityExpressionProperty(qty)
          ? isExpressionValue(qty.count)
            ? qty.count
            : undefined
          : undefined;
        // Re-save: proposal already stores this override on a formula row while EL still shows inherit.
        const isDuplicateFormulaOverrideResave =
          count?.value === change.overrideValue &&
          isFormulaDrivenExpressionQuantityCount(count) &&
          displayHint.displayResolved !== change.overrideValue;
        if (!isDuplicateFormulaOverrideResave) {
          result.push(change);
        }
        continue;
      }

      const shouldKeepOnTree = (tree: OneOfElements | undefined): boolean => {
        if (!tree || isBuildingVersionElement(tree)) return false;
        return !isElementQuantityValueChangeNoopForElement(tree, change);
      };

      const shouldKeepFormulaQuantityOverrideOnTree = (
        tree: OneOfElements | undefined,
      ): boolean => {
        if (!tree || isBuildingVersionElement(tree)) return false;
        if (isElementQuantityValueChangeNoopForElement(tree, change)) {
          return false;
        }
        const qty = getElementQuantityRecord(tree)[change.quantityName];
        if (!isElementQuantityExpressionProperty(qty)) return false;
        const count = isExpressionValue(qty.count) ? qty.count : undefined;
        if (isFormulaDrivenExpressionQuantityCount(count)) return true;
        if (mustPersistStaleExpressionQuantityValueOverride(tree, change)) {
          return true;
        }
        // Enriched rows may omit `expression` while `resolved` still shows inherit (~2028 gfa).
        if (
          count?.resolved !== undefined &&
          count.resolved !== change.overrideValue
        ) {
          return true;
        }
        const fallback = isExpressionValue(qty.fallbackCount)
          ? qty.fallbackCount
          : undefined;
        if (
          fallback?.resolved !== undefined &&
          fallback.resolved !== change.overrideValue
        ) {
          return true;
        }
        return false;
      };

      const trees = [resolved, baseline];

      if (
        trees.some(
          (tree) =>
            shouldKeepOnTree(tree) ||
            shouldKeepFormulaQuantityOverrideOnTree(tree),
        )
      ) {
        result.push(change);
        continue;
      }

      if (allowedProposalOnlyIds?.has(elementId)) {
        result.push(change);
        continue;
      }

      // Enriched EL rows can expose quantity state that proposal-only resolve trees lack.
      const hasQuantityRow = trees.some((tree) => {
        if (!tree || isBuildingVersionElement(tree)) return false;
        const qty = getElementQuantityRecord(tree)[change.quantityName];
        return isElementQuantityExpressionProperty(qty);
      });
      // Only keep override-without-row when the target element exists on baseline or
      // resolved — otherwise we would append actions that fail proposal validation.
      if (!hasQuantityRow && change.overrideValue !== undefined) {
        const existsOnBaseline = Boolean(baseline);
        const existsOnResolved = Boolean(resolved);
        if (existsOnBaseline || existsOnResolved) {
          result.push(change);
        }
      }
      continue;
    }

    if (change.type === ProjectChangeType.ElementRecipeApply) {
      const baselineElement = getElementById(version, elementId, false);
      if (
        baselineElement &&
        isElement(baselineElement) &&
        isRedundantElementRecipeApplyChange(baselineElement, change)
      ) {
        continue;
      }
    }

    const element = getElementSnapshotForNoOpCheck(
      version,
      elementId,
      change,
      resolvedVersion,
    );
    if (!element) {
      // Stale element reference — do not persist on a new proposal action
      if (change.type === ProjectChangeType.ElementActivate) {
        result.push(change);
        continue;
      }
      // Proposal-only structural addition (e.g. DEV-3293 recipe child): no baseline node
      // to compare against; keep the change so it survives onto the proposal action list.
      if (allowedProposalOnlyIds?.has(elementId)) {
        result.push(change);
      }
      continue;
    }
    if (isBuildingVersionElement(element)) {
      result.push(change);
      continue;
    }
    const partial = projectChangeToElementPartial(element, change);
    // partial === undefined means the change would not alter the element → drop it
    if (partial === undefined) continue;
    result.push(change);
  }
  return result;
};

/**
 * Parent element ids whose stored {@link ProjectChangeType.ElementRecipeApply} was removed
 * between two change lists (typically because the apply became baseline-redundant).
 */
const collectDroppedRecipeApplyParentIds = (
  previousChanges: readonly ProjectChange[],
  nextChanges: readonly ProjectChange[],
): Set<string> => {
  const remainingParentIds = new Set(
    nextChanges
      .filter((change) => change.type === ProjectChangeType.ElementRecipeApply)
      .map((change) => getChangeAffectedElementId(change))
      .filter((id): id is string => Boolean(id)),
  );
  const dropped = new Set<string>();
  for (const change of previousChanges) {
    if (change.type !== ProjectChangeType.ElementRecipeApply) continue;
    const parentId = getChangeAffectedElementId(change);
    if (parentId && !remainingParentIds.has(parentId)) dropped.add(parentId);
  }
  return dropped;
};

/**
 * When stored {@link ElementRecipeApply} rows are dropped as baseline-redundant, strip
 * proposal-only edits on `:r:` children under those parents from every action on the
 * affected proposals (DEV-3316). Edits targeting ids that already exist on baseline are kept.
 */
const stripOrphanedRecipeScopedChangesAfterDroppedApplies = (
  version: Baseline,
  actionsBefore: readonly Action[],
  actionsAfter: readonly Action[],
): { version: Baseline; actions: Action[]; mutated: boolean } => {
  const droppedParentsByActionId = new Map<string, Set<string>>();
  const actionByIdAfter = new Map(
    actionsAfter.map((action) => [action.id, action]),
  );

  for (const action of actionsBefore) {
    const after = actionByIdAfter.get(action.id);
    const dropped = collectDroppedRecipeApplyParentIds(
      action.changes,
      after?.changes ?? [],
    );
    if (dropped.size > 0) droppedParentsByActionId.set(action.id, dropped);
  }

  if (droppedParentsByActionId.size === 0) {
    return { version, actions: [...actionsAfter], mutated: false };
  }

  const proposals = version.proposals;
  if (!proposals?.length) {
    return { version, actions: [...actionsAfter], mutated: false };
  }

  const prefixesByProposal = new Map<ProposalId, string[]>();
  for (const proposal of proposals) {
    const prefixes: string[] = [];
    for (const actionId of proposal.actionIds ?? []) {
      const droppedParents = droppedParentsByActionId.get(actionId);
      if (!droppedParents) continue;
      for (const parentId of droppedParents) {
        prefixes.push(`${parentId}${RECIPE_CHILD_ID_INFIX}`);
      }
    }
    if (prefixes.length > 0) prefixesByProposal.set(proposal.id, prefixes);
  }

  if (prefixesByProposal.size === 0) {
    return { version, actions: [...actionsAfter], mutated: false };
  }

  const actionIdToProposalIds = new Map<string, ProposalId[]>();
  for (const proposal of proposals) {
    for (const actionId of proposal.actionIds ?? []) {
      const list = actionIdToProposalIds.get(actionId) ?? [];
      list.push(proposal.id);
      actionIdToProposalIds.set(actionId, list);
    }
  }

  const baselineIds = new Set(flattenElements(version).map((node) => node.id));
  let mutated = false;

  const nextActions = actionsAfter.map((action) => {
    const proposalIds = actionIdToProposalIds.get(action.id);
    if (!proposalIds?.length) return action;

    const prefixes = proposalIds.flatMap(
      (proposalId) => prefixesByProposal.get(proposalId) ?? [],
    );
    if (prefixes.length === 0) return action;

    const nextChanges = action.changes.filter((change) => {
      if (change.inactive) return true;
      const elementId = getChangeAffectedElementId(change);
      if (!elementId) return true;
      if (baselineIds.has(elementId)) return true;
      if (!prefixes.some((prefix) => elementId.startsWith(prefix))) return true;
      mutated = true;
      return false;
    });

    if (nextChanges.length === action.changes.length) return action;
    return { ...action, changes: nextChanges };
  });

  return { version, actions: nextActions, mutated };
};

/**
 * Collects every element/product id visible on baseline or a proposal-resolved tree.
 */
const buildValidElementIdsForProposal = (
  version: Baseline,
  proposalId: ProposalId,
): Set<string> => {
  const ids = new Set(flattenElements(version).map((node) => node.id));
  const { version: resolved } = resolveVersionForProposal(version, proposalId);
  for (const node of flattenElements(resolved)) {
    ids.add(node.id);
  }
  return ids;
};

/**
 * Maps each {@link Action.id} to proposal ids that list it in {@link IProposal.actionIds}.
 */
const buildActionIdToProposalIds = (
  version: Baseline,
): Map<string, ProposalId[]> => {
  const map = new Map<string, ProposalId[]>();
  for (const proposal of version.proposals ?? []) {
    for (const actionId of proposal.actionIds) {
      const list = map.get(actionId) ?? [];
      list.push(proposal.id);
      map.set(actionId, list);
    }
  }
  return map;
};

/**
 * True when {@link elementId} exists on baseline or on any proposal-resolved tree that
 * references {@link actionId}.
 */
const isElementIdValidForStoredProposalAction = (
  version: Baseline,
  actionId: string,
  elementId: string,
  actionIdToProposalIds: ReadonlyMap<string, ProposalId[]>,
  validIdsByProposal: ReadonlyMap<ProposalId, Set<string>>,
  unionValidIds: ReadonlySet<string>,
): boolean => {
  const proposalIds = actionIdToProposalIds.get(actionId);
  if (!proposalIds?.length) return unionValidIds.has(elementId);

  for (const proposalId of proposalIds) {
    const valid = validIdsByProposal.get(proposalId);
    if (valid?.has(elementId)) return true;
  }
  return false;
};

/**
 * Drops stored proposal {@link ProjectChange} rows whose {@link getChangeAffectedElementId}
 * targets a node absent from every referencing proposal-resolved tree and baseline.
 */
export const stripStaleProposalChangeElementReferences = (
  version: Baseline,
): { version: Baseline; removed: number } => {
  const actions = version.actions;
  if (!actions?.length) return { version, removed: 0 };

  const proposals = version.proposals ?? [];
  const validIdsByProposal = new Map<ProposalId, Set<string>>();
  const unionValidIds = new Set<string>();
  for (const proposal of proposals) {
    const valid = buildValidElementIdsForProposal(version, proposal.id);
    validIdsByProposal.set(proposal.id, valid);
    for (const id of valid) unionValidIds.add(id);
  }

  const actionIdToProposalIds = buildActionIdToProposalIds(version);
  let removed = 0;
  let anyChanged = false;

  const nextActions = actions.map((action) => {
    const nextChanges: ProjectChange[] = [];
    for (const change of action.changes) {
      // Inactive changes are stale by definition — kept for audit until squash (DEV-3640)
      if (change.inactive) {
        nextChanges.push(change);
        continue;
      }
      const elementId = getChangeAffectedElementId(change);
      if (
        elementId &&
        !isElementIdValidForStoredProposalAction(
          version,
          action.id,
          elementId,
          actionIdToProposalIds,
          validIdsByProposal,
          unionValidIds,
        )
      ) {
        removed += 1;
        anyChanged = true;
        continue;
      }
      nextChanges.push(change);
    }
    if (nextChanges.length === action.changes.length) return action;
    anyChanged = true;
    return { ...action, changes: nextChanges };
  });

  if (!anyChanged) return { version, removed: 0 };
  return { version: { ...version, actions: nextActions }, removed };
};

/**
 * Re-filters every stored {@link Action} on {@link version} with
 * {@link dropNoOpChangesAgainstBaseline}, then {@link compactActions}. Call after baseline
 * mutations so proposal actions never retain redundant edits that already match the raw element tree.
 *
 * Proposal-only element ids (see {@link getProposalOnlyElementIds}) are preserved through the
 * baseline-existence check so structural recipe children created by a proposal-scoped
 * {@link ProjectChangeType.ElementRecipeApply} keep their dependent edits (DEV-3293).
 *
 * When a stored {@link ProjectChangeType.ElementRecipeApply} is dropped as baseline-redundant,
 * proposal-only `:r:` child edits on the same proposal are stripped too (DEV-3316).
 *
 * Orphaned edits on removed or re-stamped element ids are stripped as well so stored proposals
 * stay valid after baseline tree mutations.
 *
 * @param version - Building version whose `actions` / `proposals` may be updated
 * @returns Same reference when no action change was removed; otherwise updated version
 */
export const dropRedundantChangesFromStoredProposalActions = (
  version: Baseline,
): Baseline => {
  const actions = version.actions;
  if (!actions?.length) return version;

  const proposalOnlyIds = getProposalOnlyElementIds(version);

  let anyFiltered = false;
  const filteredActions = actions.map((action) => {
    // Inactive changes are no-ops against baseline by definition; keep them for
    // audit until squash instead of letting the no-op filter drop them (DEV-3640)
    const activeChanges = action.changes.filter((change) => !change.inactive);
    const filtered = dropNoOpChangesAgainstBaseline(
      version,
      activeChanges,
      proposalOnlyIds,
    );
    if (filtered.length === activeChanges.length) return action;
    anyFiltered = true;
    const inactiveChanges = action.changes.filter((change) => change.inactive);
    return { ...action, changes: [...filtered, ...inactiveChanges] };
  });

  const stripped = stripOrphanedRecipeScopedChangesAfterDroppedApplies(
    version,
    actions,
    filteredActions,
  );

  const staleStripped = stripStaleProposalChangeElementReferences({
    ...version,
    actions: stripped.actions,
  });

  if (!anyFiltered && !stripped.mutated && staleStripped.removed === 0) {
    return version;
  }
  return compactActions(staleStripped.version);
};

/**
 * Runs {@link dropRedundantChangesFromStoredProposalActions} on each building version in {@link project}.
 * Invoked from {@link applyBatchChanges} only after `element.update` partials are applied
 * (baseline property/quantity values changed), so other batch kinds are unaffected.
 *
 * @param project - Project snapshot after element update partials
 * @returns Updated project when any version changed; otherwise {@link project}
 */
export const dropRedundantChangesFromStoredProposalActionsInProject = (
  project: Project,
): Project => {
  const versions = getAllBuildingVersions(project);
  const patches: Baseline[] = [];
  for (const v of versions) {
    const pruned = dropRedundantChangesFromStoredProposalActions(v);
    if (pruned !== v) patches.push(pruned);
  }
  if (patches.length === 0) return project;
  return updateBuildingVersionProperties(project, ...patches);
};

/**
 * Remaps one element id when duplicating a building version.
 *
 * Baseline `:r:` children use the index-paired {@link getElementIdMap} entry so restamped ids
 * stay aligned when legacy rows still embed an old recipe uuid in the address. Proposal-only
 * `:r:` targets (absent from the source baseline flatten) swap the parent prefix instead,
 * because index pairing can otherwise map them to a plain parent uuid (DEV-3316).
 */
export const remapDuplicateElementId = (
  id: string,
  elementMap: ReadonlyMap<string, string>,
  sourceBaselineElementIds?: ReadonlySet<string>,
): string => {
  const direct = elementMap.get(id);

  if (direct && sourceBaselineElementIds?.has(id)) {
    return direct;
  }

  const parentId = getRecipeScopedParentElementId(id);
  if (parentId) {
    const mappedParent = elementMap.get(parentId);
    if (mappedParent) {
      return `${mappedParent}${id.slice(parentId.length)}`;
    }
  }

  if (direct) return direct;

  return id;
};

/**
 * Remaps record keys (e.g. {@link IProposal.resultsRecord}) with {@link remapDuplicateElementId}.
 */
export const mapDuplicateElementIdRecordKeys = <
  T extends Record<string, unknown>,
>(
  obj: T,
  elementMap: ReadonlyMap<string, string>,
  sourceBaselineElementIds?: ReadonlySet<string>,
): T => {
  if (elementMap.size === 0) return obj;
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      remapDuplicateElementId(key, elementMap, sourceBaselineElementIds),
      value,
    ]),
  ) as T;
};

/**
 * Remaps a list of element ids using {@link remapDuplicateElementId}.
 */
const remapElementIdList = (
  ids: readonly string[],
  elementMap: ReadonlyMap<string, string>,
  sourceBaselineElementIds?: ReadonlySet<string>,
): string[] =>
  ids.map((id) =>
    remapDuplicateElementId(id, elementMap, sourceBaselineElementIds),
  );

/**
 * Returns a copy of a {@link ProjectChange} with `elementId`/`versionId` rewritten when the
 * maps contain an entry; ids not listed in a map are kept unchanged (same as how
 * {@link getElementIdMap} omits preserved element ids).
 *
 * Also remaps nested element-id lists on category-enrich changes (`removeChildIds`) so
 * duplicated building versions keep valid proposal actions (DEV-3302).
 *
 * Used after duplicating a building version to keep stored actions pointing at the new
 * cloned ids instead of the source ones where ids changed.
 */
export const remapProjectChangeElementIds = (
  change: ProjectChange,
  elementMap: ReadonlyMap<string, string>,
  versionMap: ReadonlyMap<string, string>,
  sourceBaselineElementIds?: ReadonlySet<string>,
): ProjectChange => {
  let next: ProjectChange = change;
  let changed = false;

  if ('elementId' in change && guards.is.string(change.elementId)) {
    const mapped = remapDuplicateElementId(
      change.elementId,
      elementMap,
      sourceBaselineElementIds,
    );
    // getElementIdMap only lists ids that changed; preserved ids are absent — keep elementId as-is (same as versionId handling).
    if (mapped !== change.elementId) {
      next = { ...next, elementId: mapped } as ProjectChange;
      changed = true;
    }
  }

  if ('versionId' in change && guards.is.string(change.versionId)) {
    const mapped = versionMap.get(change.versionId);
    if (mapped) {
      next = { ...next, versionId: mapped } as ProjectChange;
      changed = true;
    }
  }

  if (change.type === ProjectChangeType.ElementCategoryEnrich) {
    const remappedRemoveChildIds = remapElementIdList(
      change.removeChildIds,
      elementMap,
      sourceBaselineElementIds,
    );
    if (
      remappedRemoveChildIds.some(
        (id, index) => id !== change.removeChildIds[index],
      )
    ) {
      next = {
        ...next,
        removeChildIds: remappedRemoveChildIds,
      } as ProjectChange;
      changed = true;
    }
  }

  // Drop the cached recipe-apply resolved element when ids change — its descendant ids
  // come from the source subtree and would no longer match the target. The pipeline
  // recomputes it on the next enrich pass.
  if (
    changed &&
    next.type === ProjectChangeType.ElementRecipeApply &&
    next.resolvedElement
  ) {
    const { resolvedElement: _stale, ...rest } = next;
    next = rest as ProjectChange;
  }

  return changed ? next : change;
};

/**
 * Applies {@link remapProjectChangeElementIds} to every change in every action. Unmapped `elementId`s are
 * left unchanged (preserved elements share ids with the source). Drops resulting empty
 * actions. Used by {@link createBuildingVersion} when a version is duplicated with
 * regenerated ids so that stored {@link Baseline.actions} keep referencing the new
 * element/version ids where those ids changed.
 */
export const remapActionsElementIds = (
  actions: Action[] | undefined,
  proposals: IProposal[] | undefined,
  elementMap: ReadonlyMap<string | undefined, string | undefined> | undefined,
  versionMap: ReadonlyMap<string | undefined, string | undefined> | undefined,
  sourceBaselineElementIds?: ReadonlySet<string>,
): {
  actions: Action[] | undefined;
  proposals: IProposal[] | undefined;
} => {
  if (!actions?.length) return { actions, proposals };
  if (!elementMap?.size && !versionMap?.size) return { actions, proposals };

  // Strip undefined keys/values defensively — IElementVersionMap is typed as
  // Map<string | undefined, string | undefined> but in practice only string→string
  // entries are useful for remapping change ids.
  const safeElementMap = new Map<string, string>();
  for (const [k, v] of elementMap ?? []) {
    if (k !== undefined && v !== undefined) safeElementMap.set(k, v);
  }
  const safeVersionMap = new Map<string, string>();
  for (const [k, v] of versionMap ?? []) {
    if (k !== undefined && v !== undefined) safeVersionMap.set(k, v);
  }

  const droppedActionIds = new Set<string>();
  const nextActions: Action[] = [];

  for (const action of actions) {
    const remappedChanges: ProjectChange[] = [];
    for (const change of action.changes) {
      remappedChanges.push(
        remapProjectChangeElementIds(
          change,
          safeElementMap,
          safeVersionMap,
          sourceBaselineElementIds,
        ),
      );
    }
    const changesWithoutStaleRecipeCache =
      stripRecipeResolvedElement(remappedChanges);
    if (changesWithoutStaleRecipeCache.length === 0) {
      droppedActionIds.add(action.id);
      continue;
    }
    nextActions.push({ ...action, changes: changesWithoutStaleRecipeCache });
  }

  const nextProposals = proposals?.map((proposal) => {
    if (droppedActionIds.size === 0) return proposal;
    const ids = proposal.actionIds ?? [];
    const filtered = ids.filter((id) => !droppedActionIds.has(id));
    if (filtered.length === ids.length && proposal.actionIds !== undefined) {
      return proposal;
    }
    return { ...proposal, actionIds: filtered };
  });

  return { actions: nextActions, proposals: nextProposals };
};

/**
 * Walks {@link beforeProject} for each element id about to be removed and groups the full
 * descendant id sets by their owning building version. The result is meant to be consumed
 * by {@link cleanupActionsForRemovedElements} after the removal has been performed (the
 * tree-walk has to happen on the *before* state because the elements are gone afterwards).
 *
 * Removal ids that resolve to a building version itself are skipped — when a whole version
 * is removed its actions go with it.
 */
export const collectRemovedElementIdsByVersion = (
  beforeProject: Project,
  removalIds: ReadonlyArray<string>,
): Map<string, Set<string>> => {
  const result = new Map<string, Set<string>>();
  if (removalIds.length === 0) return result;

  for (const id of removalIds) {
    const element = getElementById(beforeProject, id, false);
    if (!element || isBuildingVersionElement(element)) continue;

    const path = getPathToElement(beforeProject, id, false);
    const owningVersion = path?.[0];
    if (!owningVersion || !isBuildingVersionElement(owningVersion)) continue;

    const set = result.get(owningVersion.id) ?? new Set<string>();
    for (const descendant of flattenElements(element)) {
      set.add(descendant.id);
    }
    result.set(owningVersion.id, set);
  }

  return result;
};

/**
 * Same as {@link collectRemovedElementIdsByVersion} but for a single
 * {@link OneOfParentElements} root (used by `applyProjectChangesToParent` where the engine
 * already has a version-scoped working object).
 */
export const collectRemovedElementIds = (
  parent: OneOfParentElements,
  removalIds: ReadonlyArray<string>,
): Set<string> => {
  const result = new Set<string>();
  if (removalIds.length === 0) return result;

  for (const id of removalIds) {
    const element = getElementById(parent, id, false);
    if (!element) continue;
    for (const descendant of flattenElements(element)) {
      result.add(descendant.id);
    }
  }
  return result;
};

/**
 * Looks up each affected version in {@link afterProject} and applies
 * {@link cleanupActionsForRemovedElements}, returning a project with the cleaned versions
 * patched in. Returns the same reference when nothing changed.
 */
export const cleanupActionsForRemovedElementsInProject = (
  afterProject: Project,
  removedIdsByVersion: ReadonlyMap<string, Set<string>>,
): Project => {
  if (removedIdsByVersion.size === 0) return afterProject;

  const updates: Baseline[] = [];
  for (const [versionId, removedIds] of removedIdsByVersion) {
    if (removedIds.size === 0) continue;
    const version = getBuildingVersionById(afterProject, versionId);
    if (!version) continue;
    if (!version.actions?.length && !version.proposals?.length) continue;

    const cleaned = cleanupActionsForRemovedElements(version, removedIds);
    if (cleaned !== version) updates.push(cleaned);
  }
  if (updates.length === 0) return afterProject;
  return updateBuildingVersionProperties(afterProject, ...updates);
};
